import { Link } from "react-router-dom";
import ContactForm from "../components/contactform/ContactForm";
import styles from "../styles/Home.module.css";

const formula = [
    { name: "Caffeine", amount: "75 mg" },
    { name: "L-Citrulline", amount: "5,000 mg" },
    { name: "Vitamin C", amount: "350 mg" },
    { name: "Creatine", amount: "2 g" },
    { name: "Magnesium Glycinate", amount: "100 mg" },
    { name: "Pink Himalayan Salt", amount: "750 mg" },
    { name: "Potassium Chloride", amount: "300 mg" },
    { name: "L-Theanine", amount: "150 mg" },
    { name: "Added Sugar", amount: "10 g" },
];

const reasons = [
    {
        ingredient: "Caffeine · 75 mg",
        title: "We don't overdose the caffeine.",
        copy: "Most preworkout brands bury you in stimulants and starve the pump. We won't trade veins and fullness for a bigger number on the label that just makes you more anxious.",
    },
    {
        ingredient: "L-Citrulline · 5,000 mg",
        title: "A pump that shows up hungover.",
        copy: "Five grams every serving. A 190+ lb user takes in close to 10,000 mg — a real, visible pump even when last night left the tank running on empty.",
    },
    {
        ingredient: "Electrolytes · loaded",
        title: "Water alone won't save you.",
        copy: "After a night out, chugging water isn't enough. Your muscles need potassium and magnesium to drag that water back where it belongs — into the muscle.",
    },
    {
        ingredient: "L-Theanine · 150 mg",
        title: "Kills the jitters. Keeps the focus.",
        copy: "Anxiety after alcohol is bad. Anxiety after caffeine and alcohol ruins the day. A scientifically backed 2:1 theanine-to-caffeine ratio smooths the whole thing out.",
    },
    {
        ingredient: "Added Sugar · 10 g",
        title: "Sugar, on purpose. No apology.",
        copy: "The fastest-digesting carbohydrate on earth, used deliberately to revive exhausted muscles and feed them in the shortest possible window.",
    },
];

const flavors = ["Lemonade", "Blue Raspberry", "Cherry"];

function Home() {
    return (
        <main className={styles.home}>
            <section className={styles.hero} aria-labelledby="home-heading">
                <div className={styles.heroInner}>
                    <div className={styles.heroCopy}>
                        <p className={styles.mantra}>
                            <span>Party hard.</span>
                            <span>Train harder.</span>
                        </p>
                        <h1 id="home-heading">
                            <span className={styles.rage}>Rage</span>
                            <span className={styles.amp}>&amp;</span>
                            <span className={styles.recover}>Recover</span>
                        </h1>
                        <p className={styles.claim}>The first preworkout built for the morning after a night out.</p>
                        <p className={styles.heroText}>
                            The party doesn't end when you stop drinking. AFTRPRTY is officially bringing to you Rage &amp; Recover; the first ever morning after preworkout, with real hydration support, smoother focus, visible pump, and controlled energy that does not feel reckless.
                        </p>
                        <ul className={styles.rhythm} aria-label="Core benefits">
                            <li>Hydrate</li>
                            <li>Focus</li>
                            <li>Pump</li>
                            <li>Recover</li>
                        </ul>
                        <div className={styles.actions}>
                            <Link className={styles.primaryAction} to="/onlineStore">Order now</Link>
                            <a className={styles.secondaryAction} href="#formula">See what's inside</a>
                        </div>
                    </div>

                    <aside className={styles.heroPanel} aria-label="Formula snapshot">
                        <p className={styles.eyebrow}>Built for the next morning</p>
                        <h2>Full formula. No mystery.</h2>
                        <dl className={styles.highlights}>
                            <div><dt>75 mg</dt><dd>Caffeine</dd></div>
                            <div><dt>5 g</dt><dd>L-Citrulline</dd></div>
                            <div><dt>10 g</dt><dd>Added sugar</dd></div>
                            <div><dt>Loaded</dt><dd>Electrolytes</dd></div>
                        </dl>
                        <p className={styles.formulaNote}>
                            Controlled caffeine. Loaded electrolytes. Sugar on purpose. No blends. No guesswork.
                        </p>
                        <p className={styles.orderPromise}>Orders promised by 11/01/26</p>
                        <ul className={styles.flavors} aria-label="Flavors">
                            {flavors.map((flavor) => <li key={flavor}>{flavor}</li>)}
                        </ul>
                    </aside>
                </div>
            </section>

            <section id="formula" className={styles.formulaSection} aria-labelledby="formula-heading">
                <div className={styles.sectionInner}>
                    <div className={styles.sectionIntro}>
                        <p className={styles.eyebrow}>01 — Know what you're drinking</p>
                        <h2 id="formula-heading">No blends. No mystery.</h2>
                        <p className={styles.formulaDeck}>Every milligram on the label. Nothing buried in a proprietary blend.</p>
                        <p className={styles.formulaCopy}>The formula reads like a printed can, not a supplement fairy tale. What goes in is exactly what you get.</p>
                    </div>

                    <div className={styles.factsWrap}>
                        <p className={styles.eyebrow}>Official label</p>
                        <div className={styles.facts} aria-label="Supplement facts">
                            <h3>Supplement Facts</h3>
                            <p>Serving size 1 scoop</p>
                            <div className={styles.factsRule} />
                            <p className={styles.factsAmount}>Amount per serving</p>
                            {formula.map((item) => (
                                <div className={styles.factRow} key={item.name}>
                                    <span>{item.name}</span>
                                    <strong>{item.amount}</strong>
                                </div>
                            ))}
                            <div className={styles.factsRule} />
                            <p className={styles.factsFoot}>{flavors.join(" · ")}</p>
                        </div>
                    </div>

                    <div className={styles.recipeWrap}>
                        <p className={styles.recipeLabel}>Recipe at a glance</p>
                        <ul className={styles.recipe} aria-label="Formula at a glance">
                            {formula.map((item) => (
                                <li key={item.name}>
                                    <strong>{item.amount}</strong>
                                    <span>{item.name}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <section id="why-it-works" className={styles.whySection} aria-labelledby="why-heading">
                <div className={styles.contentWidth}>
                    <div className={styles.whyHeading}>
                        <p className={styles.eyebrow}>02 — Why it actually works</p>
                        <h2 id="why-heading">Five ingredients. Five reasons it hits.</h2>
                        <p className={styles.sectionDeck}>Every ingredient has a job. None of them are filler.</p>
                    </div>
                    <ol className={styles.reasonList}>
                        {reasons.map((reason, index) => (
                            <li key={reason.title}>
                                <span className={styles.number}>{String(index + 1).padStart(2, "0")}</span>
                                <div className={styles.reasonText}>
                                    <div className={styles.reasonCopy}>
                                        <p className={styles.ingredient}>{reason.ingredient}</p>
                                        <h3>{reason.title}</h3>
                                    </div>
                                    <p>{reason.copy}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            <section id="how-to-use" className={styles.ritualSection} aria-labelledby="ritual-heading">
                <div className={styles.ritualInner}>
                    <div>
                        <p className={styles.eyebrow}>03 — Find your serving</p>
                        <h2 id="ritual-heading">Start with a regular serving.</h2>
                        <p className={styles.ritualDeck}>One scoop. Water. Then train.</p>
                        <p className={styles.guidance}>
                            Athletes who are used to pump based products can benefit from 1.5 scoops. Assess tolerance with regular serving size before taking more.
                        </p>
                    </div>
                    <aside className={styles.warning} aria-label="Usage warning">
                        <p className={styles.warningOverline}>Read this first</p>
                        <h3 className={styles.warningTitle}>Built for moderate nights, not disaster recovery.</h3>
                        <div className={styles.warningBody}>
                            <p>Designed strictly for use after <strong>moderate</strong> alcohol consumption. Assess tolerance with a regular serving before taking more.</p>
                            <p>Do not consume if you are experiencing severe nausea, active vomiting, or extreme dehydration. Drink at least 24–32 oz of water.</p>
                            <p>Never take on a completely empty stomach — eat a small carbohydrate meal 30 minutes before use.</p>
                        </div>
                    </aside>
                </div>
            </section>

            <section id="contact" className={styles.contactSection} aria-labelledby="contact-heading">
                <div className={styles.contactIntro}>
                    <p className={styles.eyebrow}>04 — Get in touch</p>
                    <h2 id="contact-heading">Be in the first batch.</h2>
                    <p>Order Rage &amp; Recover for yourself, your gym, or the whole crew. Ask us about larger orders, retail, or partnerships.</p>
                    <dl className={styles.contactMeta}>
                        <div><dt>Orders promised by</dt><dd>11/01/26</dd></div>
                        <div><dt>Orders</dt><dd>Personal · Gym · Crew</dd></div>
                        <div><dt>Flavors</dt><dd>{flavors.join(" · ")}</dd></div>
                    </dl>
                </div>
                <ContactForm />
            </section>
        </main>
    );
}

export default Home;